import type { CSSProperties, ReactNode, Ref } from "react";
import { dataEl, type DataElScope } from "@/utils/dataEl";
import { ButtonPreset } from "@/constants/componentPresets";
import { Cbutton } from "../Cbutton";
import { truncateMarkdownSource } from "@cabal/shared/util/markdown-cut.js";
import { CreferencePillLabel } from "./CreferencePillLabel";
import { CreferencePillInlineCluster } from "./CreferencePillInlineCluster";
import { getCreferencePillRootStyle, REFERENCE_PILL_LABEL_LIMIT, } from "./creferencePillContentModel";
import { composeCreferenceLabel } from "./creferenceResolvedEntitySelectors";
export interface CreferencePillContentProps {
    dataElScope: DataElScope;
    canClick: boolean;
    className?: string;
    disabled: boolean;
    isHovered: boolean;
    isLoading: boolean;
    isMemberReference: boolean;
    isUnresolved: boolean;
    label: string;
    labelSuffix: string | null;
    leading: ReactNode;
    linkedLabelTextStyle: CSSProperties;
    memberColor: string | null;
    memberProviderIconPath: string;
    memberProviderSlug: string;
    onClick: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    rootRef: Ref<HTMLSpanElement>;
    style?: CSSProperties;
}
export function CreferencePillContent({ dataElScope, canClick, className, disabled, isHovered, isLoading, isMemberReference, isUnresolved, label, labelSuffix, leading, linkedLabelTextStyle, memberColor, memberProviderIconPath, memberProviderSlug, onClick, onMouseEnter, onMouseLeave, rootRef, style, }: CreferencePillContentProps): ReactNode {
    // Baked Flow tight (tweak xtuduo6): cap the label at 24 with the
    // markdown-safe `..` cut, keep the icon alone in the seal, and let the
    // whole label flow inline so it breaks where the line ends. The budget
    // covers the NAME only — a version marker is appended past the `..`,
    // because it is what tells two versions of one product apart.
    const cappedLabel = truncateMarkdownSource({
        source: label,
        limit: REFERENCE_PILL_LABEL_LIMIT,
    });
    const spokenLabel = composeCreferenceLabel({ label, labelSuffix });
    return (
    <Cbutton rootAttributes={{ "data-el": dataEl(dataElScope, "PillContent/Root") }} inline inlineRootRef={rootRef} rootClassName={className} preset={ButtonPreset.Ghost} ariaLabel={canClick ? spokenLabel : undefined} padding={0} onClick={canClick ? onClick : undefined} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} disableHoverEffects={!canClick} style={getCreferencePillRootStyle({
            disabled,
            isUnresolved,
            style,
        })} containerStyle={{ display: "inline" }}>
      <CreferencePillInlineCluster isHovered={isHovered} isMemberReference={isMemberReference} isUnresolved={isUnresolved} leading={leading} memberColor={memberColor} memberProviderIconPath={memberProviderIconPath} memberProviderSlug={memberProviderSlug}/>
      <CreferencePillLabel dataElScope={dataElScope} isHovered={isHovered} isLoading={isLoading} isUnresolved={isUnresolved} linkedLabelTextStyle={linkedLabelTextStyle} suffix={labelSuffix} text={cappedLabel}/>
    </Cbutton>
    );
}
